import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { format } from 'date-fns';
import { LOGOUT_SUCCESS, logoutAccount } from '../../../actions/logout';
import { HIDE_LOGOUT_WARNING_MODAL } from '../../../actions/ui';
import { SET_UNAUTHORIZED_MESSAGE_VISIBLE } from '../../../actions/login';
import { getLogoutWarningModalShown } from '../../../selectors/ui';
import Modal from '../../Modal';
import { PrimaryButton, SecondaryButton } from '../../buttons';
import './LogoutWarningModal.scss';

const TIMEOUT = 60 * 1000;
const SECOND = 1000;

const LogoutWarningModal = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [countdown, setCountdown] = useState(TIMEOUT);
  const [isLoggingOut, setIsLoggingOut] = useState(false); 

  const { isOpen, token } = useSelector(state => ({
    isOpen: getLogoutWarningModalShown(state),
    token: state.auth?.token
  }), shallowEqual);

  useEffect(() => {
    if (isOpen) {
       setCountdown(TIMEOUT);
       setIsLoggingOut(false); 
    }
  }, [isOpen]);

  const closeModal = useCallback(() => {
    dispatch({ type: HIDE_LOGOUT_WARNING_MODAL });
  }, [dispatch]);

  const logoutAndClose = useCallback(() => {
    if (isLoggingOut) return; 

    setIsLoggingOut(true); 

    dispatch({ type: LOGOUT_SUCCESS });
    dispatch(logoutAccount(token));
    dispatch({ type: SET_UNAUTHORIZED_MESSAGE_VISIBLE });
    localStorage.removeItem('IWK_AUTO_LOGOUT');
    
    closeModal();
    navigate('/timed-out');
  }, [dispatch, navigate, token, isLoggingOut, closeModal]); 

  useEffect(() => {
    let intervalId;
    if (isOpen && !isLoggingOut) { 
      if (countdown < SECOND) {
        logoutAndClose();
      } else {
        intervalId = setInterval(() => {
          setCountdown(prev => prev - SECOND);
        }, SECOND);
      }
    }
    return () => clearInterval(intervalId);
  }, [isOpen, countdown, logoutAndClose, isLoggingOut]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal} 
      className="logout-warning-modal"
      showCloseButton={!isLoggingOut} 
      shouldCloseOnOverlayClick={!isLoggingOut} 
    >
      <div className="logout-warning-modal__header">
        <h2>Session about to expire</h2>
      </div>
      <div className="logout-warning-modal__body">
        <p>You haven&apos;t done anything for a bit. Are you still there?</p>
        <div className="logout-warning-modal__body-countdown">
          {`00:${format(countdown, 'mm:ss')}`}
        </div>
        <p>until log out</p>
      </div>
      <div className="logout-warning-modal__footer">
        <SecondaryButton 
          onClick={logoutAndClose} 
          label="Log Out" 
          disabled={isLoggingOut} 
        />
        <PrimaryButton 
          onClick={closeModal} 
          label="I'm Here" 
          disabled={isLoggingOut} 
        />
      </div>
    </Modal>
  );
};

export default LogoutWarningModal;
